import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Search, ArrowUpDown, BellRing, AlertTriangle, Archive, ArchiveRestore } from "lucide-react";
import { fmtEur } from "@/lib/lnwHours";
import { computeTileTotals } from "@/lib/dashboardTotals";
import { syncLexwareInvoices } from "@/lib/syncLexwareInvoices";

import { canCloseTimesheet, dbErrorMessage, isReadyForBilling, isInvoiced } from "@/lib/closeRules";
import { ArchiveSection, ClosedBadge } from "@/components/admin/ArchiveSection";
import { toast } from "sonner";
import { format, differenceInCalendarDays } from "date-fns";
import { de } from "date-fns/locale";

interface TimesheetRow {
  id: string;
  order_id: string;
  lnw_number: string;
  issue_date: string;
  status: "draft" | "sent" | "approved" | "rejected";
  sent_to_email: string | null;
  sent_at: string | null;
  closed_at: string | null;
  lexware_invoice_id: string | null;
  invoiced_at: string | null;
  invoice_number: string | null;
  projects: { name: string; customers: { company: string } | null } | null;
  orders: { name: string; order_number: string | null; closed_at: string | null } | null;
  timesheet_items: { hours: number }[];
}

interface OrderRow {
  id: string;
  name: string;
  hourly_rate: number | null;
  budget_hours: number | null;
  approved_hours: number | null;
  external_hours?: number | null;
}

const statusLabel: Record<TimesheetRow["status"], string> = {
  draft: "Entwurf",
  sent: "Versendet",
  approved: "Genehmigt",
  rejected: "Abgelehnt",
};

const statusVariant: Record<TimesheetRow["status"], "secondary" | "outline" | "default" | "destructive"> = {
  draft: "secondary",
  sent: "outline",
  approved: "default",
  rejected: "destructive",
};

const formatHours = (h: number) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(h);

type StatusFilter = "all" | "billing" | "invoiced" | TimesheetRow["status"];
type SortKey = "date" | "hours" | "customer";

const PAGE_SIZE = 50;
const OVERDUE_DAYS = 7;

const filterOptions: { value: StatusFilter; label: string }[] = [
  { value: "draft", label: "Entwurf" },
  { value: "sent", label: "Versendet" },
  { value: "approved", label: "Genehmigt" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "billing", label: "Bereit zur Abrechnung" },
  { value: "invoiced", label: "Abgerechnet" },
];

const rowHours = (r: TimesheetRow) => r.timesheet_items.reduce((acc, i) => acc + Number(i.hours), 0);

const isOverdue = (r: TimesheetRow) =>
  r.status === "sent" && !!r.sent_at && differenceInCalendarDays(new Date(), new Date(r.sent_at)) > OVERDUE_DAYS;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("draft");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<TimesheetRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [remindingId, setRemindingId] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const toggleClosed = async (row: TimesheetRow) => {
    const closing = !row.closed_at;
    if (closing && !window.confirm(`Leistungsnachweis ${row.lnw_number} abschließen und archivieren?`)) return;
    const { error } = await supabase.from("timesheets").update({ closed_at: closing ? new Date().toISOString() : null }).eq("id", row.id);
    if (error) toast.error(dbErrorMessage(error, "Aktion fehlgeschlagen"));
    else {
      toast.success(closing ? "Leistungsnachweis abgeschlossen" : "Leistungsnachweis wieder geöffnet");
      load();
    }
  };

  const load = async () => {
    const [{ data, error }, { data: orderData }] = await Promise.all([
      supabase
        .from("timesheets")
        .select("id, order_id, lnw_number, issue_date, status, sent_to_email, sent_at, closed_at, lexware_invoice_id, invoiced_at, invoice_number, projects(name, customers(company)), orders(name, order_number, closed_at), timesheet_items(hours)")
        .order("created_at", { ascending: false }),
      supabase.from("orders").select("id, name, hourly_rate, budget_hours, approved_hours, external_hours"),
    ]);
    if (error) {
      console.error(error);
    } else {
      setRows((data ?? []) as unknown as TimesheetRow[]);
    }
    setOrders((orderData ?? []) as OrderRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    let disposed = false;
    let syncing = false;
    const sync = async () => {
      if (syncing || document.hidden) return;
      syncing = true;
      try {
        await syncLexwareInvoices();
      } catch (error) {
        console.error("Automatischer Lexware-Abgleich fehlgeschlagen", error);
        if (!disposed) toast.error("Lexware-Abgleich unvollständig. Rechnungsstatus konnte nicht vollständig aktualisiert werden.");
      } finally {
        if (!disposed) await load();
        syncing = false;
      }
    };
    void sync();
    window.addEventListener("focus", sync);
    const timer = window.setInterval(sync, 120_000);
    return () => { disposed = true; window.removeEventListener("focus", sync); window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, sortKey, sortAsc]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const { error: itemsError } = await supabase
      .from("timesheet_items")
      .delete()
      .eq("timesheet_id", deleteTarget.id);
    if (!itemsError) {
      const { error } = await supabase.from("timesheets").delete().eq("id", deleteTarget.id);
      if (error) {
        toast.error("Leistungsnachweis konnte nicht gelöscht werden.");
      } else {
        toast.success(`Leistungsnachweis ${deleteTarget.lnw_number} gelöscht.`);
        setDeleteTarget(null);
        await load();
      }
    } else {
      toast.error("Positionen konnten nicht gelöscht werden.");
    }
    setDeleting(false);
  };

  const sendReminder = async (row: TimesheetRow) => {
    setRemindingId(row.id);
    try {
      const { data: gen, error: genError } = await supabase.functions.invoke("lnw-send-invite", {
        body: { action: "generate", timesheetId: row.id, siteUrl: window.location.origin },
      });
      if (genError || !gen?.token) {
        toast.error("Erinnerung konnte nicht erstellt werden.");
        return;
      }
      const { data, error } = await supabase.functions.invoke("lnw-send-invite", {
        body: { action: "send", timesheetId: row.id, siteUrl: window.location.origin, token: gen.token },
      });
      if (error) {
        toast.error("Erinnerung konnte nicht gesendet werden. Prüfen Sie die E-Mail-Adresse des Ansprechpartners.");
        return;
      }
      toast.success(`Erinnerung an ${data.sentTo} gesendet.`);
      await load();
    } finally {
      setRemindingId(null);
    }
  };

  const totals = useMemo(() => computeTileTotals(rows, orders), [rows, orders]);


  const overdueCount = useMemo(() => rows.filter(isOverdue).length, [rows]);

  const lowBudgetOrders = useMemo(
    () =>
      orders.filter((o) => {
        const budget = Number(o.budget_hours ?? 0);
        if (!budget) return false;
        const remaining = budget - Number(o.approved_hours ?? 0) - Number(o.external_hours ?? 0);
        return remaining / budget < 0.1;
      }),
    [orders],
  );

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const active = rows.filter((r) => !r.closed_at);
    const base =
      statusFilter === "all"
        ? active
        : statusFilter === "billing"
          ? active.filter(isReadyForBilling)
          : statusFilter === "invoiced"
            ? active.filter(isInvoiced)
            : active.filter((r) => r.status === statusFilter);
    const searched = term
      ? base.filter((r) =>
          [
            r.lnw_number,
            r.projects?.customers?.company ?? "",
            r.projects?.name ?? "",
            r.orders?.name ?? "",
            r.orders?.order_number ?? "",
                            r.invoice_number ?? "",
          ]
            .join(" ")
            .toLowerCase()
            .includes(term),
        )
      : base;
    const sorted = [...searched].sort((a, b) => {
      let diff = 0;
      if (sortKey === "date") diff = new Date(a.issue_date).getTime() - new Date(b.issue_date).getTime();
      else if (sortKey === "hours") diff = rowHours(a) - rowHours(b);
      else diff = (a.projects?.customers?.company ?? "").localeCompare(b.projects?.customers?.company ?? "");
      return sortAsc ? diff : -diff;
    });
    return sorted;
  }, [rows, statusFilter, search, sortKey, sortAsc]);

  const archivedRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((r) => !!r.closed_at)
      .filter((r) =>
        !term ||
        [r.lnw_number, r.projects?.customers?.company ?? "", r.projects?.name ?? "", r.orders?.name ?? "", r.orders?.order_number ?? "", r.invoice_number ?? ""]
          .join(" ").toLowerCase().includes(term),
      )
      .sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime());
  }, [rows, search]);
  const showArchive = archiveOpen || (!!search.trim() && archivedRows.length > 0);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const countByStatus = useMemo(() => {
    const counts: Record<StatusFilter, number> = { all: 0, billing: 0, invoiced: 0, draft: 0, sent: 0, approved: 0, rejected: 0 };
    for (const r of rows) {
      if (r.closed_at) continue;
      counts.all += 1;
      counts[r.status] += 1;
      if (isReadyForBilling(r)) counts.billing += 1;
      if (isInvoiced(r)) counts.invoiced += 1;
    }
    return counts;
  }, [rows]);

  const renderRow = (r: TimesheetRow) => {
                  const hours = rowHours(r);
                  const overdue = isOverdue(r);
                  return (
                    <TableRow
                      key={r.id}
                      tabIndex={0}
                      className={`cursor-pointer transition-colors ${r.closed_at ? "text-muted-foreground" : ""} hover:bg-muted/60 focus-visible:bg-muted focus-visible:outline-none`}
                      onClick={() => navigate(`/admin/lnw/${r.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") navigate(`/admin/lnw/${r.id}`);
                      }}
                    >
                      <TableCell className="font-medium">{r.lnw_number}</TableCell>
                      <TableCell className="hidden md:table-cell">{r.projects?.customers?.company ?? "–"}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div>{r.projects?.name ?? "–"}</div>
                        <div className="text-xs text-muted-foreground">{r.orders?.name ?? "–"}</div>
                      </TableCell>
                      <TableCell>{format(new Date(r.issue_date), "dd.MM.yyyy", { locale: de })}</TableCell>
                      <TableCell className="text-right">{formatHours(hours)} h</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={statusVariant[r.status]}>{statusLabel[r.status]}</Badge>
                          {isInvoiced(r) && <Badge variant="secondary">Abgerechnet</Badge>}
                          {r.closed_at && <ClosedBadge closedAt={r.closed_at} />}
                          {overdue && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" /> überfällig
                            </Badge>
                          )}
                        </div>
                        {r.invoice_number && <div className="mt-1 text-xs text-muted-foreground">{r.invoice_number}</div>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {r.closed_at ? (
                            <span title={r.orders?.closed_at ? "Auftrag ist abgeschlossen, bitte zuerst wieder öffnen" : "Wieder öffnen"}>
                              <Button variant="ghost" size="icon" aria-label={`${r.lnw_number} wieder öffnen`} disabled={!!r.orders?.closed_at}
                                onClick={(e) => { e.stopPropagation(); toggleClosed(r); }}>
                                <ArchiveRestore className="h-4 w-4" />
                              </Button>
                            </span>
                          ) : canCloseTimesheet(r.status).ok && (
                            <Button variant="ghost" size="icon" aria-label={`${r.lnw_number} abschließen`} title="Abschließen und archivieren"
                              onClick={(e) => { e.stopPropagation(); toggleClosed(r); }}>
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          {r.status === "sent" && !r.closed_at && (
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Erinnerung für ${r.lnw_number} senden`}
                              disabled={remindingId === r.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                sendReminder(r);
                              }}
                            >
                              <BellRing className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Leistungsnachweis ${r.lnw_number} bearbeiten`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/lnw/${r.id}`);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {!r.closed_at && <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Leistungsnachweis ${r.lnw_number} löschen`}
                            className="text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(r);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((prev) => !prev);
    else {
      setSortKey(key);
      setSortAsc(key === "customer");
    }
  };

  return (
    <>
      <Helmet>
        <title>Übersicht | Leistungsnachweise</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Leistungsnachweise</h1>
        <Link to="/admin/lnw/neu">
          <Button className="gap-2">
            <Plus className="h-4 w-4" /> Neuer Leistungsnachweis
          </Button>
        </Link>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Offene Stunden</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{formatHours(totals.open)} h</p>
            <p className="text-xs text-muted-foreground">{fmtEur(totals.openEur)} Auftragswert</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Genehmigte Stunden</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{formatHours(totals.approved)} h</p>
            <p className="text-xs text-muted-foreground">{fmtEur(totals.approvedEur)} Auftragswert</p>
          </CardContent>
        </Card>
        <Card className={overdueCount > 0 ? "border-destructive/40" : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Freigabe überfällig</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-semibold ${overdueCount > 0 ? "text-destructive" : ""}`}>{overdueCount}</p>
            <p className="text-xs text-muted-foreground">seit mehr als {OVERDUE_DAYS} Tagen versendet</p>
          </CardContent>
        </Card>
        <Card className={lowBudgetOrders.length > 0 ? "border-destructive/40" : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Aufträge fast ausgeschöpft</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-semibold ${lowBudgetOrders.length > 0 ? "text-destructive" : ""}`}>
              {lowBudgetOrders.length}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {lowBudgetOrders.length > 0 ? lowBudgetOrders.map((o) => o.name).join(", ") : "unter 10 % Reststunden"}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setStatusFilter("all")}
          className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
            statusFilter === "all"
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-muted-foreground border-border hover:bg-muted"
          }`}
        >
          Alle ({countByStatus.all})
        </button>
        {filterOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setStatusFilter(opt.value)}
            className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            {opt.label} ({countByStatus[opt.value]})
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setArchiveOpen(true);
            setTimeout(() => document.getElementById("lnw-archive")?.scrollIntoView({ behavior: "smooth" }), 50);
          }}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-border bg-background px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
        >
          <Archive className="h-3.5 w-3.5" /> Archiviert ({rows.filter((r) => r.closed_at).length})
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Kunde, LNW-Kennung oder Auftrag"
            className="pl-9"
            aria-label="Leistungsnachweise durchsuchen"
          />
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => toggleSort("date")}>
          <ArrowUpDown className="h-4 w-4" /> Datum
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => toggleSort("hours")}>
          <ArrowUpDown className="h-4 w-4" /> Stunden
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={() => toggleSort("customer")}>
          <ArrowUpDown className="h-4 w-4" /> Kunde
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>LNW-Kennung</TableHead>
                <TableHead className="hidden md:table-cell">Kunde</TableHead>
                <TableHead className="hidden lg:table-cell">Projekt / Auftrag</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead className="text-right">Stunden</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center">
                    <p className="font-medium">
                      {rows.length === 0 ? "Noch keine Leistungsnachweise" : "Keine Treffer"}
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {rows.length === 0
                        ? "Legen Sie zuerst einen Kunden, ein Projekt und einen Auftrag an."
                        : "Passen Sie Suche oder Statusfilter an."}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map(renderRow)
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!loading && archivedRows.length > 0 && (
        <div id="lnw-archive">
          <ArchiveSection count={archivedRows.length} open={showArchive} onOpenChange={setArchiveOpen}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>LNW-Kennung</TableHead>
                  <TableHead className="hidden md:table-cell">Kunde</TableHead>
                  <TableHead className="hidden lg:table-cell">Projekt / Auftrag</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Stunden</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{archivedRows.map(renderRow)}</TableBody>
            </Table>
          </ArchiveSection>
        </div>
      )}

      {!loading && filteredRows.length > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredRows.length)} von {filteredRows.length}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Zurück
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
              Weiter
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leistungsnachweis löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  Der Leistungsnachweis <strong>{deleteTarget.lnw_number}</strong>
                  {deleteTarget.projects?.customers?.company && (
                    <> ({deleteTarget.projects.customers.company})</>
                  )} wird samt aller Positionen unwiderruflich gelöscht.
                  {deleteTarget.status === "approved" && (
                    <> Die genehmigten Stunden werden automatisch vom Auftrag zurückgerechnet.</>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Löschen…" : "Endgültig löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminDashboard;
